/**
 * Single declarative navigation config — replaces the legacy app's dual
 * menu systems. Filtered by role + ACL + module entitlement at render time
 * (see filter-nav.ts).
 *
 * # Convention: Add buttons on listing pages
 *
 * Sidebar groups DO NOT include "Add X" / "Create X" sub-items. Where a
 * group used to have an Add+List pair the entry collapses to a single
 * leaf pointing at the listing route. The "+ Add X" action lives on the
 * listing page itself via `PageHeader` `actions`.
 *
 * # Module gating
 *
 * `requiresAnyModuleId?: number[]` declares the moduleIds that must be
 * present in the current user's entitlement set for the item to show.
 * If any of the listed IDs is in the set, the item passes. SADMIN /
 * SUB_SUPER_ADMIN bypass module gating entirely (see filter-nav.ts).
 *
 * Module-ID cheat sheet (from legacy):
 *   1  = Custom IVR              (USER)
 *   3  = Voice Webhooks          (USER)
 *   4  = API Management          (USER)
 *   5  = Dedicated CLI           (ADMIN)
 *   6  = Inbound Campaigns       (USER, ADMIN)
 *   7  = TTS (Text-to-Speech)    (USER)
 *   9  = SMS Webhooks            (USER)
 *   10 = TTS Flow / Continuous   (USER)
 *   11, 12 = Report Scheduling   (USER)
 */
import {
  BookAudio,
  ChartArea,
  Cog,
  FileBarChart,
  FileText,
  Globe,
  Headphones,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Megaphone,
  PhoneCall,
  PhoneOutgoing,
  Server,
  ShieldCheck,
  Users,
  Webhook,
  Wifi,
  Bell,
  type LucideIcon,
} from 'lucide-react';
import { USER_ROLES, type UserRole } from '@/lib/constants/roles';

export interface NavLeaf {
  label: string;
  to: string;
  icon?: LucideIcon;
  roles: UserRole[];
  /** Dotted path into the ACL tree, e.g. "Manage Users.Create User". */
  permissionPath?: string;
  /**
   * Module IDs that grant access. If any of these is in the current
   * user's module set, the item shows. Ignored for SADMIN /
   * SUB_SUPER_ADMIN, who get module-independent access.
   */
  requiresAnyModuleId?: number[];
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  roles: UserRole[];
  permissionPath?: string;
  requiresAnyModuleId?: number[];
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

const { ADMIN, USER, S_ADMIN, SUB_USER, SUB_SUPER_ADMIN } = USER_ROLES;

export const NAV_ITEMS: NavItem[] = [
  /* ------------- Top of sidebar ----------------------------------- */
  {
    label: 'Overview',
    icon: LayoutDashboard,
    to: '/',
    roles: [ADMIN, USER, S_ADMIN, SUB_SUPER_ADMIN],
  },
  {
    label: 'My Activity',
    icon: FileBarChart,
    to: '/sub-user-report',
    roles: [SUB_USER],
  },
  {
    label: 'Credit Ledger',
    icon: History,
    to: '/credits-history',
    roles: [USER],
  },
  {
    label: 'Health',
    icon: Server,
    to: '/monitoring',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Server Monitoring',
  },

  /* ------------- People ------------------------------------------- */

  {
    label: 'Users',
    icon: Users,
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Users',
    children: [
      // Listing — also hosts the "+ Add User" button (→ /create-user).
      { label: 'Directory', to: '/user-list', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Resellers', to: '/reseller-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Credit Ledger', to: '/credits-history', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Call Records', to: '/call-detail-records', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Tier Pricing', to: '/user-settings', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Plan Audit', to: '/voice-plan-logs', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },

  /* ------------- Dialers (SADMIN) --------------------------------- */

  {
    label: 'Dialers',
    icon: Headphones,
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Dialers',
    children: [
      { label: 'Search', to: '/search-dialer', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      // Listing — "+ Create Dialer" lives top-right.
      { label: 'All Dialers', to: '/dialer-list', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Usage', to: '/dialer-usage', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Usage Rollup', to: '/dialer-usage-rollup', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },

  /* ------------- Daily operations (campaigns + builders) ---------- */

  {
    label: 'Voice',
    icon: PhoneOutgoing,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Campaign',
    children: [
      // Listing — also hosts "+ Compose Campaign" button (→ /add-voice-campaign).
      { label: 'Live', to: '/voice-campaign-summary', roles: [USER] },
      { label: 'History', to: '/historical-voice-campaign-summary', roles: [USER] },
      { label: 'Scheduled', to: '/schedule-voice-campaign-summary', roles: [USER] },
      { label: 'Operations', to: '/campaign-operations', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN] },
      {
        label: 'Continuous (CvC)',
        to: '/cvc-summary',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [10],
      },
      { label: 'All Campaigns', to: '/voice-superadmin-campaign-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Prompt Campaigns', to: '/prompt-voice-superadmin-campaign-summary', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Archive', to: '/historical-campaign-summary-superadmin', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Allocation', to: '/manage-campaigns-superadmin', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },
  {
    label: 'TTS Studio',
    icon: Megaphone,
    roles: [USER],
    requiresAnyModuleId: [10],
    children: [
      // Listing — also hosts "+ Add TTS Flow" button.
      { label: 'Flows', to: '/tts-flow-list', roles: [USER] },
      { label: 'Run Logs', to: '/tts-flow-logs', roles: [USER] },
    ],
  },
  // Add+List pair → collapsed. "+ Add Custom IVR" lives on /ivr-list top-right.
  {
    label: 'IVR Designer',
    icon: PhoneCall,
    to: '/ivr-list',
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    requiresAnyModuleId: [1],
  },
  {
    // Agent management — its own module. Used by Inbound Campaigns, but
    // not nested under it.
    label: 'Agents',
    icon: Headphones,
    to: '/group-agent-list',
    roles: [USER],
  },

  /* ------------- Content + reporting ------------------------------ */

  {
    label: 'Library',
    icon: BookAudio,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      // SADMIN review queue — first so it's the default landing for approvers.
      { label: 'Pending Prompts', to: '/all-voice-files', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      // Listing — also hosts "+ Add Voice File" button.
      { label: 'Recordings', to: '/voice-files', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      {
        label: 'TTS Clips',
        to: '/tts-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [7],
      },
    ],
  },
  {
    label: 'Reports',
    icon: FileText,
    roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      { label: 'Downloads', to: '/download-report', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'CDR', to: '/call-detail-records', roles: [USER] },
      {
        label: 'Scheduled Reports',
        to: '/report-scheduling',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [11, 12],
      },
    ],
  },

  /* ------------- Number lists ------------------------------------- */

  {
    label: 'Number Lists',
    icon: ListChecks,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      // Listing — Add+View pair collapsed; "+ Add to Blacklist" lives top-right.
      { label: 'Blacklist', to: '/voice-blacklist', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Bulk Add Blacklist', to: '/bulk-voice-blacklist', roles: [USER] },
      { label: 'Blacklist & DND', to: '/add-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Bulk Blacklist & DND', to: '/bulk-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      // Listing — Add+View pair collapsed; "+ Add Whitelist" lives top-right.
      { label: 'Whitelist', to: '/view-whitelist', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Lookup Number', to: '/search-blacklist-dnd', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },

  /* ------------- Pricing ------------------------------------------ */

  // Add+View pair → collapsed leaf. "+ Add Voice Plan" lives on /voice-plans top-right.
  {
    label: 'Voice Pricing',
    icon: Wifi,
    to: '/voice-plans',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },
  // Add+View pair → collapsed leaf. "+ Add TTS Plan" lives on /tts-plans top-right.
  {
    label: 'TTS Pricing',
    icon: Wifi,
    to: '/tts-plans',
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
  },

  /* ------------- Team / Admins (role-specific shortcuts) ---------- */

  // Add+List pair → collapsed leaf. Add lives on /sub-user-list top-right.
  {
    label: 'Team',
    icon: Users,
    to: '/sub-user-list',
    roles: [USER],
  },
  // Add+List pair → collapsed leaf. Add lives on /sub-super-admin/user-list top-right.
  {
    label: 'Admins',
    icon: ShieldCheck,
    to: '/sub-super-admin/user-list',
    roles: [S_ADMIN],
  },

  /* ------------- Connectivity ------------------------------------- */

  {
    label: 'CLI Pool',
    icon: PhoneCall,
    roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage CLI',
    requiresAnyModuleId: [5],
    children: [
      { label: 'Allocate', to: '/allocate-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      // Listing — "+ Upload CLI" lives on /cli-list top-right.
      { label: 'All CLIs', to: '/cli-list', roles: [ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Pilots', to: '/cli-pilot-list', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Archive', to: '/archived-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Dedicated Archive', to: '/archived-dedicated-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },
  // USER role — single archived-CLI list → leaf.
  {
    label: 'Archive',
    icon: PhoneCall,
    to: '/user-archive-cli-list',
    roles: [USER],
  },
  {
    label: 'API CLI',
    icon: Globe,
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage API CLI',
    children: [
      // Listing — "+ Upload API CLI" lives top-right.
      { label: 'All API CLIs', to: '/cli-api-list', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Pilots', to: '/cli-api-pilot-list', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'Archive', to: '/archived-api-cli', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },
  {
    label: 'API Keys',
    icon: KeyRound,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    requiresAnyModuleId: [4],
    children: [
      // Listing — "+ Create API Key" lives top-right.
      { label: 'Keys', to: '/user-api-key-list', roles: [USER] },
      { label: 'Usage', to: '/api-key-summary-list', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'History', to: '/api-key-historical-summary-list', roles: [USER, S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },
  {
    label: 'Webhooks',
    icon: Webhook,
    roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
    children: [
      {
        label: 'Voice',
        to: '/webhook-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [3],
      },
      {
        label: 'SMS',
        to: '/sms-webhook-list',
        roles: [USER, S_ADMIN, SUB_SUPER_ADMIN],
        requiresAnyModuleId: [9],
      },
    ],
  },

  /* ------------- Infrastructure (SADMIN) -------------------------- */

  // Add + Bulk Add + Bulk Delete actions live on /group-list top-right.
  {
    label: 'Groups',
    icon: Users,
    to: '/group-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Groups',
  },
  // Add + Bulk Add + Bulk Delete actions live on /location-list top-right.
  {
    label: 'Locations',
    icon: MapPin,
    to: '/location-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
    permissionPath: 'Manage Locations',
  },
  // Add+List pair → collapsed leaf. "+ Create Trunk" lives on /trunk-list top-right.
  {
    label: 'Trunks',
    icon: ChartArea,
    to: '/trunk-list',
    roles: [S_ADMIN, SUB_SUPER_ADMIN],
  },

  /* ------------- Admin tools (SADMIN) ----------------------------- */

  {
    label: 'Access Control',
    icon: Cog,
    roles: [S_ADMIN],
    children: [
      { label: 'Modules', to: '/module', roles: [S_ADMIN] },
      { label: 'Roles', to: '/acl-settings', roles: [S_ADMIN] },
    ],
  },
  {
    label: 'Announcements',
    icon: Bell,
    to: '/flash-messages',
    roles: [S_ADMIN],
  },

  /* ------------- Inbound (placed last per product order) ---------- */

  {
    label: 'Inbound',
    icon: PhoneCall,
    roles: [ADMIN, USER, S_ADMIN, SUB_SUPER_ADMIN],
    // USER + ADMIN need module 6; SADMIN sees Inbound unconditionally.
    requiresAnyModuleId: [6],
    children: [
      // Listing — also hosts "+ New Inbound Campaign" button (→ /add-inbound-campaign).
      { label: 'Live', to: '/inbound-campaign-list', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'History', to: '/inbound-campaign-historical-list', roles: [USER, ADMIN, S_ADMIN, SUB_SUPER_ADMIN] },
      { label: 'All Inbound', to: '/inbound-superadmin-campaign-summary', roles: [S_ADMIN, SUB_SUPER_ADMIN] },
    ],
  },

  // Hidden routes still mounted but not surfaced in sidebar:
  //  - /user-profile           (header dropdown)
  //  - /update-user/:id, /update-group/:id, etc.  (action buttons)
  //  - /create-user, /create-group, /add-voice-plan, ...
  //    All "+ Add" pages — reached from listing page top-right buttons,
  //    never directly from the sidebar.
];
